import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DownloadedFile } from '@digilize/shared/definitions/src';
import { DataApiResponseLegacy } from '@digilize/shared/definitions/src/lib/interfaces/DataApiResponseLegacy';
import {
  GetSubscriptionInvoicesQuery,
  SubscriptionInvoiceListModel,
  SubscriptionInvoicePaidOutModel,
  SubscriptionInvoiceTotalsModel,
  SubscriptionInvoiceWaiveModel,
  SubscriptionInvoiceWaiveResultModel,
} from '@digilize/shared/definitions/src/lib/models';
import { map, Observable } from 'rxjs';

import { toDownloadedFile } from './downloaded-file';

/* The totals and the export describe the whole filtered set, so the paging and ordering of the page
   being looked at are dropped before the call: the backend ignores them, and the URL should say what
   the call means. Empty values go too, so a filter left blank is not sent as "undefined". */
const PAGING_KEYS = ['length', 'start', 'draw', 'order_column_name', 'order_column_descending'];
const withoutPaging = (params: GetSubscriptionInvoicesQuery = {}): Record<string, any> =>
  Object.fromEntries(
    Object.entries(params).filter(([key, value]) => !PAGING_KEYS.includes(key) && value !== null && value !== undefined)
  );

@Injectable({
  providedIn: 'root',
})
export class SubscriptionInvoicesApiService {
  private readonly BASE_URL = '/api/v1.0/subscription-invoices';

  constructor(private http: HttpClient) {}

  /* Invoices and credit notes across every instance the caller may see. DataApiResponseLegacy and
     not DataApiResponse: DataTablesList names recordsTotal/recordsFiltered explicitly, so the
     backend's snake_case naming strategy leaves them camelCase on the wire even though swagger
     documents them snake_case - the same trap as SubscriptionInstancesApiService.
     Omitting company_id is correct for a moderator: the backend already scopes them to the
     companies they are a member of, and a Pauly admin sees every tenant. */
  getInvoices = (params?: GetSubscriptionInvoicesQuery) =>
    this.http.get<DataApiResponseLegacy<SubscriptionInvoiceListModel[]>>(this.BASE_URL, {
      params: params as Record<string, any>,
    });

  /* The whole filtered set summed, for the line above the invoices grid (PAUL-3817). Same filters as
     getInvoices; `open_amount` is only there with `unpaid_on`. */
  getTotals = (params?: GetSubscriptionInvoicesQuery) =>
    this.http.get<SubscriptionInvoiceTotalsModel>(`${this.BASE_URL}/totals`, { params: withoutPaging(params) });

  /* The same set as an xlsx: one sheet, one row per document in the grid's order. The backend names
     the file (`subscription-invoices_<day>.xlsx`, or `..._unpaid-on-<day>.xlsx`); a failure's body is
     a blob too, so read it with AlertService.handleBlobApiError. */
  exportInvoices = (params?: GetSubscriptionInvoicesQuery): Observable<DownloadedFile> =>
    this.http
      .get(`${this.BASE_URL}/export`, { params: withoutPaging(params), observe: 'response', responseType: 'blob' })
      .pipe(map(toDownloadedFile));

  /* Records - or takes back - the payout for a credit note. Pauly does not move the money: the
     refund is made by hand in the Pay.nl dashboard and this is where that gets written down.
     Returns 200 with no body, and the row can have moved underneath the caller in the meantime (a
     late SEPA debit landing, a bank reversal), so re-read the grid rather than assuming the new
     status. */
  setPaidOut = (id: string, body: SubscriptionInvoicePaidOutModel) =>
    this.http.put<void>(`${this.BASE_URL}/${id}/paid-out`, body);

  /* The one payout Pauly makes itself: asks Pay. to refund a credit note in the payout queue on the
     payment that paid the invoice it credits. No body. Answers 200 with no body and the note moves
     to payout_state Requested; Pay. reports the rest over the next working days, so re-read the
     grid rather than assuming a status. The refusals are mapped in payout-error.util.ts. */
  payout = (id: string) => this.http.post<void>(`${this.BASE_URL}/${id}/payout`, null);

  /* Waives part or all of what is still owed on an invoice (PAUL-3827): the backend writes a credit
     note carrying the operator and the reason, and settles the invoice once nothing is left. `id` is
     the invoice, never a credit note. The refusals are mapped in invoice-waiver.util.ts. */
  waive = (id: string, body: SubscriptionInvoiceWaiveModel): Observable<SubscriptionInvoiceWaiveResultModel> =>
    this.http.post<SubscriptionInvoiceWaiveResultModel>(`${this.BASE_URL}/${id}/waive`, body);
}
